import {
  marketsData,
  defaultMarketData,
} from "@local/offer/src/lib/const/marketsData"

interface Props {
  value?: number | null
  market?: string
  /**
   * ISO code to render the value in, when it is not the browsing market's
   * currency (e.g. a past order in the account area, which is denominated in
   * whatever it was charged in). Omit and the market's symbol is used, which
   * is correct anywhere market and amount always agree.
   */
  currencyCode?: string
  className?: string
  noCurrencySymbol?: boolean
  noConvertFromCent?: boolean
  decimals?: number
  /** Screen-reader-only label rendered before the value (e.g. "Original price"). */
  srLabel?: string
}

const getCountryData = (market: string) => {
  const countryData = marketsData.find(
    (data) => data.market === market.toLowerCase()
  )
  if (!countryData) {
    return defaultMarketData
  }
  return countryData
}

/**
 * Symbol for an explicit ISO code. Falls back to the code itself (e.g. "SEK ")
 * rather than a wrong symbol when the currency isn't one we sell in.
 */
const getCurrencySymbol = (currencyCode: string): string =>
  marketsData.find((data) => data.currencyCode === currencyCode)
    ?.currencySymbol ?? `${currencyCode} `

export const Money = ({
  value,
  market,
  currencyCode,
  className,
  noCurrencySymbol,
  noConvertFromCent,
  decimals = 2,
  srLabel,
}: Props) => {
  const { currencySymbol: marketSymbol } = getCountryData(market || "us")
  // An explicit currency always wins: the amount is denominated in it, whatever
  // market the visitor happens to be browsing.
  const currencySymbol = currencyCode
    ? getCurrencySymbol(currencyCode)
    : marketSymbol

  if (value == null || isNaN(value)) {
    return null
  }

  const isNegative = value < 0
  const absoluteValue = noConvertFromCent
    ? Math.abs(value)
    : Math.abs(value) / 100

  return (
    <span className={className}>
      {srLabel ? <span className="sr-only">{srLabel} </span> : null}
      {isNegative && "-"}
      {!noCurrencySymbol && currencySymbol}&#8288;
      <span>{absoluteValue.toFixed(decimals)}</span>
    </span>
  )
}
